import moment from 'moment';
import { mapState } from 'vuex';
import { codemirror } from 'vue-codemirror';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { themesMixin } from '@/utils/themes';
import LineChartMixin from '@/components/core/dtwin/lineChartMixin';
import { relativeBreakpoint } from '@/utils/vuex';
import ApiService from '@/services/apiService';
import { calculateAxisValue } from '@/utils/charts';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/base16-dark.css';
import 'codemirror/theme/duotone-light.css';
import 'codemirror/mode/javascript/javascript.js';
const CHART_BEGIN_ZERO = false;
const chartPreviewConfig = {
  legend: {
    display: false
  },
  scales: {
    yAxes: [{
      ticks: {
        // begin y-axis at zero if possbile depending on CHART_BEGIN_ZERO setting
        ...(CHART_BEGIN_ZERO ? {
          suggestedMin: 0
        } : {}),
        fontSize: 8
      },
      gridLines: {
        display: true
      }
    }],
    xAxes: [{
      type: 'time',
      distribution: 'series',
      time: {
        tooltipFormat: 'YYYY-MM-DD HH:mm',
        unit: 'day',
        displayFormats: {
          day: 'D.M.'
        }
      },
      ticks: {
        autoSkip: true,
        fontSize: 10
      },
      gridLines: {
        display: false
      }
    }]
  }
};
const cmOptionsDefaults = {
  mode: 'application/text',
  tabSize: 4,
  lineNumbers: false,
  line: true,
  lineWrapping: true,
  foldGutter: true,
  styleActiveLine: true,
  styleSelectedText: true,
  readOnly: true,
  hintOptions: {
    completeSingle: false
  },
  viewportMargin: Infinity
};
export default {
  name: 'CoreDtwinStatePreviewDbTiles',
  components: {
    codemirror
  },
  mixins: [relativeBreakpoint, LineChartMixin, themesMixin],
  props: {
    twinDataStates: {
      type: Array,
      required: true
    },
    updatedStates: {
      type: Array,
      required: false,
      default: () => []
    },
    favCard: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    noDataAlert: {
      type: Boolean,
      required: false,
      default: false
    },
    tileSizeSetting: {
      type: Number,
      required: false,
      default: 0
    },
    chartDotRadius: {
      type: Number,
      required: false,
      default: 2
    },
    overflow: {
      type: Boolean,
      required: false,
      default: false
    },
    // only together with overflow
    maxHeight: {
      type: Number,
      required: false,
      default: 450
    }
  },
  data: function () {
    return {
      previewJsonData: null,
      previewStringData: null,
      showJsonDialog: false,
      showStringDialog: false,
      historyState: null,
      showHistoryDialog: false,
      cmOptionsJson: {
        ...cmOptionsDefaults,
        ...{
          theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
          mode: 'application/ld+json',
          lineWrapping: false
        }
      },
      cmOptionsString: {
        ...cmOptionsDefaults,
        ...{
          theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
          mode: 'application/text',
          lineWrapping: true
        }
      },
      chartDataFormated: {},
      chartLoading: false,
      multiChartConfig: {}
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions']),
    colsPerTile() {
      if (this.relativeBreakpoint.width > 1100) {
        return 3;
      }
      if (this.relativeBreakpoint.width > 800) {
        return 4;
      }
      if (this.relativeBreakpoint.width > 510) {
        return 6;
      }
      return 12;
    },
    boxStyle() {
      let result = '';
      if (this.tileSizeSetting) {
        result = `${result}margin-left: 7px; margin-right: 7px;`;
      }
      if (this.overflow) {
        result = `${result}max-height: ${this.maxHeight}px; overflow: auto;`;
      }
      return result;
    },
    previewVisual() {
      if (this.tileSizeSetting === 1) {
        return 'preview-tile-visual';
      }
      return 'preview-visual';
    },
    previewBatteryHeight() {
      if (this.tileSizeSetting === 1) {
        return 83;
      }
      return 160;
    },
    previewAlertHeight() {
      if (this.tileSizeSetting === 1) {
        return 70;
      }
      return 140;
    },
    uiComponents() {
      if (this.favCard && this.tileSizeSetting === 1) {
        return true; // FavCard and Big Tiles
      } else if (this.favCard && this.tileSizeSetting === 0) {
        return false; // FavCard and small Tiles
      } else if (!this.favCard && this.tileSizeSetting === 0) {
        return true; // No FavCard and D-Twin or other view
      }
      return true;
    },
    hasStateEditPermission() {
      return (this.permissions || {})['DigitalTwin.manage'] || (this.permissions || {})['DigitalTwin.write'];
    },
    tiles() {
      if (!this.twinDataStates) {
        return [];
      }
      let twinDataStates = [].concat(this.twinDataStates);
      twinDataStates.sort((a, b) => a.properties.orderIdx - b.properties.orderIdx);
      let sanitizedStates = [];
      for (let i = 0; i < twinDataStates.length; i++) {
        const state = twinDataStates[i];
        if (get(state, 'properties.valueType') === 'boolean') {
          // for each boolean state, add a button to toggle <--- that is the past
          // nowadays we jst show a nice colored icon and no possibility to toggle a button
          const modifierBoolTile = {
            ...state,
            type: 'modifier-bool'
          };
          twinDataStates[i] = modifierBoolTile;
        }

        // Delete Geo-Coordnates if linked to DTWin Position or if visualisation-type is "hidden"
        if (!state.properties.linkGeoCoordinate && this.getComponentType(twinDataStates[i]) !== 'hidden') {
          if (!twinDataStates[i].properties.visual) {
            twinDataStates[i].properties.visual = {};
          }
          if (!twinDataStates[i].properties.visual.options) {
            twinDataStates[i].properties.visual.options = {};
          }
          if (twinDataStates[i].value === undefined) {
            twinDataStates[i].value = {};
          }
          sanitizedStates.push(twinDataStates[i]);
        }
      }
      return sanitizedStates;
    }
  },
  watch: {
    updatedStates() {
      if (this.historyState && this.updatedStates.findIndex(s => s.id === this.historyState.id) >= 0) {
        if (this.$refs['dtwin-state-history']) {
          this.$refs['dtwin-state-history'].reloadData();
          this.filteredDashboardData(this.twinDataStates);
        }
      }
    },
    twinDataStates(updated, old) {
      if (!isEqual(updated, old)) {
        this.fetchChartData();
      }
    }
  },
  async created() {
    this.fetchChartData();
  },
  methods: {
    get,
    calculateAxisValue,
    fetchChartData() {
      if (!this.twinDataStates) {
        return;
      }
      this.chartLoading = true;
      const promises = this.filteredDashboardData(this.twinDataStates).map(item => {
        this.setMultiConfigStateData(item);
        // MWa Debug console.log(item);
        return this.loadChartData(item);
      });
      return Promise.all(promises).then(results => {
        return results.map(item => {
          this.chartDataFormated[item.objModel.id] = this.formatMultipleData(item.objModel, item.responseData);
          // force-redraw the whole linechart component
          item.objModel.lineChartId = new Date().toISOString();
          return this.chartDataFormated[item.objModel.id];
        });
      }).then(data => {
        this.chartLoading = false;
        return data;
      }).catch(err => console.error(err));
    },
    getCompStyle(twinDataState) {
      if (this.getComponentType(twinDataState) === 'lineChart') {
        // IF tileSizeSetting is 1 then never show line charts with a span greater than 3
        if (this.tileSizeSetting === 1) {
          return {
            gridColumn: 'span 3'
          };
        }
        if (this.$vuetify.breakpoint.lgAndUp) {
          return {
            gridColumn: `span ${this.getSpan(twinDataState)}`
          };
        }
        return {
          gridColumn: 'span 2'
        };
      }
      return {};
    },
    getComponentType(state) {
      if (!state) {
        return null;
      }
      const typeState = get(state, 'properties.valueType');
      const typeReal = typeof get(state, 'value.value');
      const typeVisual = get(state, 'properties.visual.type');

      // if a visual type was set via visual config then use that one
      if (typeVisual) {
        return typeVisual;
      }
      if (state.type === 'modifier-bool') {
        return 'modifier-bool';
      }
      if (typeState === 'button') {
        return 'button';
      }

      // if a the states type is json and real type is string or object than use json
      if (typeState === 'json' && (typeReal === 'string' || typeReal === 'object')) {
        return 'json';
      }

      // otherwise return based on the real type of the value or the states type
      // 'odometer' for numbers and 'string' to display everything else
      switch (typeReal || typeState) {
        case 'number':
          return 'fittext';
        default:
          return 'string';
      }
    },
    getSpan(state) {
      return Math.min(get(state, 'properties.visual.options.span'), 5) || 2;
    },
    getLowerBound(state, defaultValue) {
      if (state.properties.lowerBound === undefined) {
        return defaultValue;
      }
      return parseFloat(state.properties.lowerBound);
    },
    getUpperBound(state, defaultValue) {
      if (state.properties.upperBound === undefined) {
        return defaultValue;
      }
      return parseFloat(state.properties.upperBound);
    },
    getThresholds(state, defaultMin, defaultMax) {
      const min = this.getLowerBound(state, defaultMin);
      const max = this.getUpperBound(state, defaultMax);
      return [min + (max - min) * 0.1, max - (max - min) * 0.5];
    },
    modifierBoolButtonColor(boolSwitchData) {
      if (get(boolSwitchData, 'value.value') === true) {
        return this.color;
      }
      return undefined;
    },
    showJSON(a) {
      this.previewJsonData = this.outputValue(a);
      this.showJsonDialog = true;
    },
    showString(s) {
      this.previewStringData = String(s).trim();
      this.showStringDialog = true;
    },
    showHistory(a) {
      this.historyState = a;
      this.showHistoryDialog = true;
    },
    outputValue(item) {
      if (item === null) {
        return 'null';
      }
      try {
        const jsonObject = typeof item === 'object' ? item : JSON.parse(item);
        // parse and then stringify again to get a nice format
        return JSON.stringify(jsonObject, null, 1);
      } catch (error) {
        // on error we return outside of try/catch
      }
      return item;
    },
    localizeDate(a) {
      if (a) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        if (this.tileSizeSetting === 0) {
          return moment(a).format('L LTS');
        }
        return moment(a).format('L');
      }
      return '-';
    },
    setMultiConfigStateData(objModel) {
      if (!isEmpty(objModel.properties.visual.options)) {
        let chartLinesCurved = has(objModel.properties.visual.options, 'chartLinesCurved') ? objModel.properties.visual.options.chartLinesCurved : true;
        let chartOmitNull = has(objModel.properties.visual.options, 'chartOmitNull') ? objModel.properties.visual.options.chartOmitNull : true;
        const start = moment(get(objModel, 'properties.visual.options.queryStartDate'));
        const end = moment(get(objModel, 'properties.visual.options.queryEndDate'));
        // We have to check whether to display days or hours in the xAxis ticks
        const differenceInDays = end.diff(start, 'days');
        let chartPreviewConfigVar = cloneDeep(chartPreviewConfig);
        // Display hours if we have a day or less
        if (differenceInDays <= 1) {
          chartPreviewConfigVar.scales.xAxes[0].time.unit = 'hour';
          chartPreviewConfigVar.scales.xAxes[0].displayFormats = {};
        }
        this.multiChartConfig[objModel.id] = {
          ...(chartLinesCurved ? {
            ...chartPreviewConfigVar,
            elements: {
              line: {
                tension: 0.5
              },
              point: {
                pointStyle: 'circle',
                radius: this.chartDotRadius
              }
            }
          } : {
            ...chartPreviewConfigVar,
            elements: {
              line: {
                tension: 0
              }
            }
          }),
          ...(chartOmitNull ? {} : {
            spanGaps: true
          })
        };
      }
    },
    faIcon(dtItem) {
      return get(dtItem, 'value.value') ? get(dtItem, 'properties.visual.options.boolVisual.firstFaIcon') : get(dtItem, 'properties.visual.options.boolVisual.secondFaIcon');
    },
    faIconColor(dtItem) {
      return get(dtItem, 'value.value') ? get(dtItem, 'properties.visual.options.boolVisual.firstColor') : get(dtItem, 'properties.visual.options.boolVisual.secondColor');
    },
    stateTitle(dtItem) {
      return get(dtItem, 'value.value') ? get(dtItem, 'properties.visual.options.boolVisual.firstStateTitle') : get(dtItem, 'properties.visual.options.boolVisual.secondStateTitle');
    },
    isBooleanVisualisation(state) {
      return get(state, 'properties.valueType') === 'boolean' && !get(state, 'properties.visual.options.type') && get(state, 'properties.visual.options.boolVisual') !== undefined && get(state, 'properties.visual.options.boolVisual.firstStateTitle') !== '' && get(state, 'properties.visual.options.boolVisual.firstFaIcon') !== '' || get(state, 'properties.visual.options.boolVisual.secondStateTitle') !== '' && get(state, 'properties.visual.options.boolVisual.secondFaIcon') !== '';
    },
    modifierBoolButtonIcon(boolSwitchData) {
      if (get(boolSwitchData, 'value.value') === true || get(boolSwitchData, 'value.value') === 1) {
        return 'fal fa-toggle-on';
      }
      return 'fal fa-toggle-off';
    },
    modifierBoolButtonClicked(boolSwitchData) {
      boolSwitchData.value.value = !boolSwitchData.value.value;
      ApiService.digitalTwinUpdateStateValue(this, {
        id: boolSwitchData.id,
        dtwinId: boolSwitchData.dtwinId,
        value: get(boolSwitchData, 'value.value'),
        skipTransformer: true
      }).then(() => {
        // this.$emit('didUpdateStates', [boolSwitchData.id]);
      }).catch(error => {
        console.error(error);
      });
    }
  }
};